'use client';

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:localcoin": true
    });
  } catch (e) {}
}();
import { PrismicNextImage } from '@prismicio/next';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrevNextButtons, PrevButton, NextButton } from '@/components/molecules/TestimonialsCarousel/ArrowButtons';
import { StepsSliderProps } from './types';
function hasBackContent(flipsideDescription?: string | null, flipsideImageUrl?: string | null) {
  return !!(flipsideDescription?.trim() || flipsideImageUrl);
}
interface CardInteractionProps {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  role?: 'button';
  tabIndex?: 0;
}
interface CardInteractionOptions {
  index: number;
  isHoverTrigger: boolean;
  isClickTrigger: boolean;
  setFlippedState: (index: number, isFlipped: boolean) => void;
  toggleFlippedState: (index: number) => void;
}
function getCardInteractionProps({
  index,
  isHoverTrigger,
  isClickTrigger,
  setFlippedState,
  toggleFlippedState
}: CardInteractionOptions): CardInteractionProps {
  if (isClickTrigger) return {
    onClick: () => toggleFlippedState(index),
    onKeyDown: event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleFlippedState(index);
      }
    },
    role: 'button',
    tabIndex: 0
  };
  if (isHoverTrigger) return {
    onMouseEnter: () => setFlippedState(index, true),
    onMouseLeave: () => setFlippedState(index, false)
  };
  return {};
}
function StepsSlider({
  items,
  className,
  isFlipEnabled = false,
  flipTrigger = 'hover'
}: StepsSliderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start'
  }, []);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi);
  const {
    t
  } = useTranslation();
  const [flippedStateByIndex, setFlippedStateByIndex] = useState<Record<number, boolean>>({});
  function setFlippedState(index: number, isFlipped: boolean) {
    setFlippedStateByIndex(currentState => {
      if (currentState[index] === isFlipped) return currentState;
      return {
        ...currentState,
        [index]: isFlipped
      };
    });
  }
  function toggleFlippedState(index: number) {
    setFlippedStateByIndex(currentState => ({
      ...currentState,
      [index]: !currentState[index]
    }));
  }
  return <div className={clsx('relative w-full', className)} data-sentry-component="StepsSlider" data-sentry-source-file="index.tsx">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-5">
          {items.map(({
          description,
          image,
          flipsideDescription,
          flipsideImage
        }, index) => {
          const canFlip = isFlipEnabled && hasBackContent(flipsideDescription, flipsideImage?.url);
          const isClickTrigger = canFlip && flipTrigger === 'click';
          const isHoverTrigger = canFlip && flipTrigger === 'hover';
          const isFlipped = canFlip && !!flippedStateByIndex[index];
          const interactionProps = getCardInteractionProps({
            index,
            isHoverTrigger,
            isClickTrigger,
            setFlippedState,
            toggleFlippedState
          });
          return <div className={clsx('relative w-65 shrink-0 overflow-visible last:mr-6 last:lg:mr-0', canFlip && 'perspective-[1000px]', isClickTrigger && 'cursor-pointer')} key={description} {...interactionProps}>
                <div className={clsx('relative min-h-74 rounded-[0.625rem] border border-gray-200 bg-white p-6 transition-transform duration-500', canFlip && 'transform-3d', canFlip && (isFlipped ? 'transform-[rotateY(180deg)]' : 'transform-[rotateY(0deg)]'))}>
                  <div className="h-full [-webkit-backface-visibility:hidden] backface-hidden">
                    <PrismicNextImage fallbackAlt="" className="mb-4 h-38.5 w-full rounded-[.625rem] object-cover" field={image} />
                    <p className="text-caption-3 mb-2 font-bold text-green-600 uppercase">
                      {t('stepByStepGuideSlider.stepLabel', {
                    stepNumber: index + 1
                  })}
                    </p>
                    <p className="text-body-5 font-semibold text-gray-600 lg:pr-14">
                      {description}
                    </p>
                  </div>
                  {canFlip ? <div className="absolute inset-0 transform-[rotateY(180deg)] rounded-[0.625rem] bg-white p-6 [-webkit-backface-visibility:hidden] backface-hidden">
                      {flipsideImage?.url ? <PrismicNextImage fallbackAlt="" className="mb-4 h-38.5 w-full rounded-[.625rem] object-cover" field={flipsideImage} /> : null}
                      {flipsideDescription && flipsideDescription.trim().length > 0 ? <p className="text-body-5 font-semibold text-gray-600">
                          {flipsideDescription}
                        </p> : null}
                    </div> : null}
                </div>
              </div>;
        })}
        </div>
      </div>
      {items.length > 1 && <div className="mt-6 flex justify-center gap-3">
          <div className={clsx(prevBtnDisabled && 'cursor-not-allowed opacity-50', 'transition-all')}>
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          </div>
          <div className={clsx(nextBtnDisabled && 'cursor-not-allowed opacity-50', 'transition-all')}>
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} progress={0} />
          </div>
        </div>}
    </div>;
}
export default StepsSlider;